import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, useContext } from '@/hooks';
import { Card, CardContent, CardHeader, CardTitle, Button, Spinner } from '@/components/ui';
import { Stethoscope, Plus, ArrowRight, ArrowLeft, Mail, Phone } from 'lucide-react';
import { Doctor } from '@/types/models';
import { doctorsService, type CreateDoctorData, type UpdateDoctorData } from '@/services/api/doctors.service';
import toast from 'react-hot-toast';

export function DoctorSelection() {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const { clinic, setDoctor } = useContext();

  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Verificação imediata: se é médico com doctor vinculado, redireciona automaticamente
  const isAutoRedirecting = user?.role === 'Médico' && user?.doctor && clinic;

  useEffect(() => {
    if (!clinic) {
      navigate('/clinicas');
      return;
    }

    // Se o usuário é médico e tem um doctor vinculado, seleciona automaticamente
    if (user?.role === 'Médico' && user?.doctor) {
      const doctorData: Doctor = {
        id: user.doctor.id,
        name: user.doctor.name,
        specialty: user.doctor.specialty || null,
        email: null,
        crm: null,
        phone: null,
        isActive: true,
        clinics: clinic ? [clinic.id] : []
      };
      setDoctor(doctorData);
      navigate('/calendar', { replace: true });
      return;
    }

    loadDoctors();
  }, [clinic, user]);

  const loadDoctors = async () => {
    if (!clinic) return;

    setIsLoading(true);
    try {
      const allDoctors = await doctorsService.getAccessible();
      
      // Filter doctors by current clinic
      const clinicDoctors = allDoctors.filter(doctor => 
        doctor.clinics && doctor.clinics.includes(clinic.id)
      );
      
      setDoctors(clinicDoctors || []);
    } catch (error) {
      toast.error('Erro ao carregar médicos');
      console.error('Failed to load doctors:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectDoctor = (doctor: Doctor) => {
    setDoctor(doctor);
    navigate('/calendar');
  };

  const handleCreateDoctor = () => {
    setIsModalOpen(true);
  };

  const handleBackToClinic = () => {
    navigate('/clinicas');
  };

  // Se está redirecionando automaticamente, mostra loading
  if (isAutoRedirecting) {
    return (
      <div className="flex items-center justify-center h-96">
        <Spinner size="lg" label="Carregando..." />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Spinner size="lg" label="Carregando médicos..." />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header with breadcrumb */}
      <div className="mb-8">
        <button
          onClick={handleBackToClinic}
          className="flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Voltar para Clínicas
        </button>

        <h1 className="text-3xl font-bold text-gray-900">
          Selecione um Médico
        </h1>
        <p className="mt-2 text-gray-600">
          Clínica: <span className="font-semibold">{clinic?.name}</span>
        </p>
      </div>

      {doctors.length === 0 ? (
        <Card variant="bordered" className="p-12 text-center">
          <div className="flex flex-col items-center">
            <Stethoscope className="h-16 w-16 text-gray-400 mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Nenhum médico encontrado
            </h2>
            <p className="text-gray-600 mb-6 max-w-md">
              {isAdmin
                ? 'Esta clínica ainda não possui médicos cadastrados. Adicione médicos para gerenciar seus agendamentos.'
                : 'Esta clínica ainda não possui médicos disponíveis. Entre em contato com o administrador.'}
            </p>
            {isAdmin && (
              <Button onClick={handleCreateDoctor} size="lg">
                <Plus className="mr-2 h-5 w-5" />
                Adicionar Primeiro Médico
              </Button>
            )}
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {doctors.map((doctor) => (
            <Card
              key={doctor.id}
              variant="bordered"
              className="hover:shadow-lg transition-shadow cursor-pointer group"
              onClick={() => handleSelectDoctor(doctor)}
            >
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="p-3 bg-green-50 rounded-lg group-hover:bg-green-100 transition-colors">
                    <Stethoscope className="h-8 w-8 text-green-600" />
                  </div>
                  <ArrowRight className="h-5 w-5 text-gray-400 group-hover:text-green-600 transition-colors" />
                </div>
              </CardHeader>
              <CardContent>
                <CardTitle className="text-lg mb-1">
                  Dr(a). {doctor.name}
                </CardTitle>

                {doctor.specialty && (
                  <p className="text-sm font-medium text-gray-700 mb-3">
                    {doctor.specialty}
                  </p>
                )}

                <div className="space-y-1">
                  {doctor.crm && (
                    <p className="text-xs text-gray-600">
                      CRM: {doctor.crm}
                    </p>
                  )}

                  {doctor.email && (
                    <div className="flex items-center text-xs text-gray-600">
                      <Mail className="h-3 w-3 mr-1" />
                      {doctor.email}
                    </div>
                  )}

                  {doctor.phone && (
                    <div className="flex items-center text-xs text-gray-600">
                      <Phone className="h-3 w-3 mr-1" />
                      {doctor.phone}
                    </div>
                  )}
                </div>

                {!doctor.isActive && (
                  <span className="inline-block mt-3 px-2 py-1 text-xs font-medium text-red-600 bg-red-50 rounded">
                    Inativo
                  </span>
                )}
              </CardContent>
            </Card>
          ))}

          {isAdmin && (
            <Card
              variant="bordered"
              className="hover:shadow-lg transition-shadow cursor-pointer group border-dashed border-2 border-gray-300"
              onClick={handleCreateDoctor}
            >
              <CardContent className="h-full flex flex-col items-center justify-center py-12">
                <div className="p-3 bg-gray-50 rounded-lg group-hover:bg-gray-100 transition-colors mb-4">
                  <Plus className="h-8 w-8 text-gray-400 group-hover:text-gray-600" />
                </div>
                <p className="text-gray-600 font-medium">
                  Adicionar Novo Médico
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Modal */}
      {isModalOpen && (
        <DoctorFormModal
          clinic={clinic}
          onClose={() => setIsModalOpen(false)}
          onSuccess={() => {
            loadDoctors();
            setIsModalOpen(false);
          }}
        />
      )}
    </div>
  );
}

// Doctor Form Modal Component
function DoctorFormModal({
  clinic,
  onClose,
  onSuccess,
}: {
  clinic: typeof useContext extends () => { clinic: infer C } ? C : never;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { user } = useAuth();
  const [formData, setFormData] = useState<CreateDoctorData>({
    name: '',
    email: '',
    crm: '',
    specialty: '',
    phone: '',
    isActive: true,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!clinic) {
      toast.error('Nenhuma clínica selecionada');
      return;
    }
    
    setIsSubmitting(true);

    try {
      // Create doctor
      const newDoctor = await doctorsService.create(formData);
      
      // Associate with current clinic
      await doctorsService.addToClinic(newDoctor.id, clinic.id);
      
      toast.success('Médico criado e associado à clínica com sucesso!');
      onSuccess();
    } catch (error) {
      console.error('Error saving doctor:', error);
      toast.error('Erro ao criar médico');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div 
      className="fixed inset-0 backdrop-blur-sm bg-white/30 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            Novo Médico
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Será associado à clínica: <strong>{clinic?.name}</strong>
          </p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nome do Médico *
            </label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Dr. João Silva"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                CRM
              </label>
              <input
                type="text"
                value={formData.crm}
                onChange={(e) => setFormData({ ...formData, crm: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="CRM-SP 123456"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Especialidade
              </label>
              <input
                type="text"
                value={formData.specialty}
                onChange={(e) => setFormData({ ...formData, specialty: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Cardiologia"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              E-mail
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="joao.silva@clinica.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Telefone
            </label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="(11) 98765-4321"
            />
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="isActive"
              checked={formData.isActive}
              onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900">
              Médico ativo
            </label>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {isSubmitting ? 'Criando...' : 'Criar Médico'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}